import React, { useEffect, useRef, useState } from 'react';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';

/**
 * Per-channel notification control for the rep reading a client group or a
 * client user's private chat (Nikhil 2026-09-13: "their group + explicits").
 * Reading a channel no longer subscribes a rep to it; a rep is notified for
 *   1. the channels of the clients they own as CS rep (Client.csOwner),
 *   2. the channels they explicitly Follow,
 *   3. @mentions and private-chat hand-offs (unchanged, not shown here).
 * The backend says which applies through
 *     channel.notify = { owner: boolean, following: boolean, muted: boolean }
 * on every staff-facing channel object (feed rows, the org's /groups list).
 * A missing `notify` (older backend) means unknown → this renders nothing.
 *   owner     → quiet "Notifying you · your client" label, no button
 *   muted     → "Muted" label (there is no unmute UI yet)
 *   otherwise → ☆ Follow / ★ Following, the ticket-follow visual language:
 *               POST / DELETE /api/v1/comms/channels/:id/follow, both answer
 *               { notify } which replaces the local state.
 */

export const OWNER_LABEL = 'Notifying you · your client';
export const OWNER_TITLE = "You get pushes / Chat DMs for this group because you are the client's CS rep";
export const FOLLOW_LABEL = '☆ Follow';
export const FOLLOWING_LABEL = '★ Following';
export const FOLLOW_TITLE = 'Get updates for this group (unread + push)';
export const UNFOLLOW_TITLE = 'Stop getting updates for this group';
export const MUTED_LABEL = 'Muted';
export const MUTED_TITLE = 'You muted this group — no pushes / Chat DMs for it';

export const follow_path = (channel_id) => `/api/v1/comms/channels/${encodeURIComponent(String(channel_id))}/follow`;

/** {owner, following, muted} as booleans, or null when the backend sent no notify. */
export const normalise_notify = (notify) => (
    notify && typeof notify === 'object'
        ? { owner: !!notify.owner, following: !!notify.following, muted: !!notify.muted }
        : null
);

/** Which control to show: 'muted' | 'owner' | 'following' | 'follow', or null (unknown → nothing).
 *  Muted wins over everything (it is the truthful state); owner wins over following. */
export const notify_mode = (notify) => {
    const n = normalise_notify(notify);
    if (!n) return null;
    if (n.muted) return 'muted';
    if (n.owner) return 'owner';
    return n.following ? 'following' : 'follow';
};

/** The state after a follow / unfollow call: the returned notify when the
 *  backend sent one, else the previous state with `following` set to what
 *  the (successful) call asked for. */
export const next_notify = (prev, resp, following) => {
    const from_resp = resp && typeof resp === 'object' ? normalise_notify(resp.notify) : null;
    if (from_resp) return from_resp;
    return { ...(normalise_notify(prev) || { owner: false, muted: false }), following: !!following };
};

const S = {
    quiet: { fontSize: 11.5, color: '#8a97a5', whiteSpace: 'nowrap', cursor: 'default' },
    btn: { fontSize: 12, padding: '2px 12px', borderRadius: 15, border: '1px solid #c9d4de', background: '#fff', color: '#3d4f5f', cursor: 'pointer', lineHeight: '18px', verticalAlign: 'middle', whiteSpace: 'nowrap' },
};

// on_change(notify): optional — lets the host patch its own copy of the
// channel row so a re-render from a later poll does not undo the click.
export default function NotifyToggle({ channel_id, notify, on_change, style }) {
    const known = normalise_notify(notify);
    const [state, set_state] = useState(known);
    const [busy, set_busy] = useState(false);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    // Re-sync when the host hands us a different channel or different values
    // (keyed on the primitives, not the object identity — feed polls rebuild
    // the row every time).
    const k_has = !!known, k_owner = !!(known && known.owner), k_following = !!(known && known.following), k_muted = !!(known && known.muted);
    useEffect(() => {
        set_state(k_has ? { owner: k_owner, following: k_following, muted: k_muted } : null);
    }, [channel_id, k_has, k_owner, k_following, k_muted]);

    const mode = notify_mode(state);
    if (!mode || channel_id == null || channel_id === '') return null;

    if (mode === 'owner') {
        return <span title={OWNER_TITLE} style={{ ...S.quiet, ...(style || {}) }}>{OWNER_LABEL}</span>;
    }
    if (mode === 'muted') {
        return <span title={MUTED_TITLE} style={{ ...S.quiet, ...(style || {}) }}>{MUTED_LABEL}</span>;
    }

    const following = mode === 'following';
    const toggle = async () => {
        if (busy) return;
        set_busy(true);
        try {
            const r = await cs_analysis_fetch({ path: follow_path(channel_id), method: following ? 'DELETE' : 'POST' });
            const next = next_notify(state, r, !following);
            if (alive.current) set_state(next);
            if (on_change) on_change(next);
        } catch (e) {
            window.alert((e && e.message) || 'could not update notifications');
        }
        if (alive.current) set_busy(false);
    };
    return (
        <button type='button' disabled={busy} onClick={toggle}
            title={following ? UNFOLLOW_TITLE : FOLLOW_TITLE}
            style={{ ...S.btn, border: '1px solid ' + (following ? '#2f6fed' : '#c9d4de'), color: following ? '#2f6fed' : '#3d4f5f', ...(style || {}) }}>
            {following ? FOLLOWING_LABEL : FOLLOW_LABEL}
        </button>
    );
}
